import { action, makeObservable, observable } from 'mobx';
import { GenexActions, GenexSurveyTypes } from 'core/enums/genex';
import { GenexModal } from 'core/services/api/surveys/genex/genex-survey';
import { StorageHelper } from 'core/services/storage';
import { _noop } from 'utils/function.utils';
import getDemographicsPayload from './genex/get-demographics-payload';
import { ModelWithCategory, OptionalUserData, Store } from './genex/interfaces';

export { ModelWithCategory, OptionalUserData };

const getCookieName = () => `${GenexStore.type}_${GenexStore.current}`;

const cookieValue = 'true';

const isGenexCookieSet = () => {
  const cookieName = getCookieName();
  return StorageHelper.GetInstance.cookieExistsAndHasValue(cookieName, cookieValue);
}

const setGenexCookie = () => {
  const cookieName = getCookieName();
  StorageHelper.GetInstance.setCookie(cookieName, cookieValue, 365);
}

//Genex = 'DStv_genex',
export const GenexStore: Store = {
  isInitialised: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  methodPointer: _noop,
  current: GenexActions.None,
  type: GenexSurveyTypes.None,
  customerNumber: '',
  userEmail: '',
  modal: new GenexModal(),
  useMAHDemographics: false,
  optionalUserData: undefined,
  surveyLink: '',
  uniqueInteractionId: '',
  initialize: (type: GenexSurveyTypes | null, userEmail: string | null, lang?: string) => {
    if (GenexStore.isInitialised) {
      return;
    }

    makeObservable(GenexStore, {
      isInitialised: observable,
      methodPointer: observable,
      triggerSurvey: action,
      registerCompleteCallback: action,
    });

    if (type !== GenexSurveyTypes.Genex && type !== GenexSurveyTypes.GenexMAH) {
      return;
    }

    GenexStore.type = GenexSurveyTypes.Genex; //GenexMAH wouldn't work but acts as a flag for the MAH demographics.
    GenexStore.useMAHDemographics = type === GenexSurveyTypes.GenexMAH;

    const baseUrl = '/survey';

    const modal: GenexModal = new GenexModal();
    modal.setConfiguration({
      parentElement: document.body,
      baseUrl: baseUrl,
    });

    GenexStore.userEmail = userEmail || '';
    GenexStore.lang = lang?.toLowerCase();

    GenexStore.registerCompleteCallback();
    GenexStore.isInitialised = true;
  },

  //We could in theory just use appContext within this controller but let's avoid the additional dependency for now.
  triggerSurvey: (
    model: ModelWithCategory,
    customerNumber: string,
    timestamp: Date,
    onStopSurvey?: (id: string, link: string) => void,
  ): void => {
    if (!GenexStore.isInitialised) {
      return;
    }

    const { category } = model;
    GenexStore.current = category;

    if (isGenexCookieSet()) {
      return;
    }

    if (!category) {
      console.log('Model or category property is missing');
      return;
    }

    if (!Object.values(GenexActions).includes(category)) {
      // The category doesn't match any of the enum values
      console.log(`Category is invalid: ${category}`);
      return;
    }

    const uniqueInteractionId: string = new Date().getTime().toString();
    const payload = getDemographicsPayload(GenexStore, model, customerNumber, timestamp);

    GenexStore.modal
      .startSurvey(uniqueInteractionId, customerNumber, payload, GenexStore.optionalUserData?.countryCode, !!onStopSurvey)
      .then((result) => {
        GenexStore.surveyLink = result?.eventInformation?.link;
        GenexStore.uniqueInteractionId = result?.uniqueInteractionId;
        onStopSurvey && onStopSurvey(result?.uniqueInteractionId, result?.eventInformation?.link);
        return result?.link;
      });
  },

  openSurvey: () => {
    const { surveyLink, uniqueInteractionId } = GenexStore;

    if (surveyLink && uniqueInteractionId) {
      GenexStore.modal.loadIframe(uniqueInteractionId, surveyLink);
    }
  },

  saveUserData: (userData: OptionalUserData) => {
    GenexStore.optionalUserData = userData;
  },

  registerCompleteCallback: () => {
    GenexStore.methodPointer('setEventCallback', (category: any, action: string, label: any, ...args: any) => {
      const setCookie = action === 'Feedback:Success' || (action === 'Feedback:Close' && !isGenexCookieSet);

      if (!setCookie) {
        return;
      }

      setGenexCookie()
    });
  },
};